import { Node, Edge } from "reactflow";
import { v4 as uuidv4 } from "uuid";
import { apiService } from "./api.service";
import { getSupabaseClient } from "../config/supabase";
import { STORAGE_BUCKET_NAME, getStorageUrl } from "@utils/utils";

const TEMPLATES_FOLDER = 'templates';

const getTemplatePreviewUrl = (filename: string) => 
  getStorageUrl(`${TEMPLATES_FOLDER}/${filename}`);

export interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  preview: string;
  nodes: Node[];
  edges: Edge[];
}

// Define the type for updating templates
export interface UpdateTemplateData {
  name?: string;
  description?: string;
  category?: string;
  nodes?: Node[];
  edges?: Edge[];
  previewFile?: File; // Optional file for updating preview
  preview?: string; // Optional existing preview filename
}


const DEFAULT_TEMPLATE: Template = {
  id: "default",
  name: "Default Setup",
  description: "Start with a basic setup that you can customize.",
  category: "Basic",
  preview: "empty.png",
  nodes: [
    {
      id: "cluster-1",
      type: "cluster",
      position: { x: 400, y: 200 },
      data: {
        label: "MetaRouter on GCP",
        environment: "development",
        modules: {
          consentEnforcement: false,
          botDetection: false,
          crossDomainTracking: false,
          dataReplay: false,
          schemaEnforcement: false,
          transformations: false,
        },
      },
    },
  ],
  edges: [],
};

class TemplateService {
  private static instance: TemplateService;

  private constructor() {}

  static getInstance(): TemplateService {
    if (!TemplateService.instance) {
      TemplateService.instance = new TemplateService();
    }
    return TemplateService.instance;
  }

  async getDefaultTemplate(): Promise<Template> {
    return DEFAULT_TEMPLATE;
  }

  async getTemplate(templateId: string): Promise<Template> {
    if (templateId === "default") {
      return {
        ...DEFAULT_TEMPLATE,
        preview: getTemplatePreviewUrl(DEFAULT_TEMPLATE.preview),
      };
    }

    let template = await apiService.getTemplate(templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }

    template = {
      ...template,
      preview: getTemplatePreviewUrl(template.preview),
    }

    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }

    // Create a mapping of old node IDs to new node IDs
    const nodeIdMap = new Map<string, string>();
    
    // First pass: create new IDs for nodes and store the mapping
    const nodesWithNewIds = template.nodes.map((node: Node) => {
      const newId = `${node.id}-${uuidv4()}`;
      nodeIdMap.set(node.id, newId);
      return {
        ...node,
        id: newId,
      };
    });
    
    // Sort nodes: Ensure parent nodes appear before their children in the array.
    // This is crucial for React Flow rendering, especially with groups.
    nodesWithNewIds.sort((a: Node, b: Node) => {
      // Check if 'a' is a child of 'b' using the NEW IDs from the map
      // Note: Using parentId property based on logged data
      if (a.parentId && nodeIdMap.get(a.parentId) === b.id) {
        return 1; // 'a' (child) should come after 'b' (parent)
      }
      // Check if 'b' is a child of 'a' using the NEW IDs from the map
      if (b.parentId && nodeIdMap.get(b.parentId) === a.id) {
        return -1; // 'b' (child) should come after 'a' (parent)
      }
      // If neither is a direct child of the other, maintain relative order.
      return 0; 
    });

    // Second pass: update edge source/target IDs using the mapping
    const edgesWithNewIds = template.edges.map((edge: Edge) => ({
      ...edge,
      source: nodeIdMap.get(edge.source) || edge.source, // Update edge source
      target: nodeIdMap.get(edge.target) || edge.target, // Update edge target
    }));

    // Third pass: Update parentId IDs on the *sorted* nodes
    const finalNodes = nodesWithNewIds.map((node: Node) => { 
      // Note: Using parentId property based on logged data
      if (node.parentId) { 
        const newParentId = nodeIdMap.get(node.parentId);
        return {
          ...node,
          // Ensure parentId is updated only if found in the map
          parentId: newParentId || node.parentId, 
        };
      }
      return node; // Return node unchanged if it has no parentId
    });

    return {
      ...template,
      nodes: finalNodes, // Use nodes with updated parentNode IDs
      edges: edgesWithNewIds,
    };
  }

  async getAllTemplates(search?: string): Promise<Template[]> {
    let templates = await apiService.getTemplates(search);
    if (!templates) {
      return [];
    }

    templates = templates.map(template => ({
      ...template,
      preview: getTemplatePreviewUrl(template.preview)
    }));
    return templates;
  }

  async uploadTemplatePreview(file: File): Promise<string> {
    const supabase = getSupabaseClient();
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `${TEMPLATES_FOLDER}/${fileName}`;
    
    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET_NAME)
      .upload(filePath, file);

    if (uploadError) {
      throw new Error(`Error uploading file: ${uploadError.message}`);
    }

    const { data: { publicUrl } } = supabase.storage
      .from(STORAGE_BUCKET_NAME)
      .getPublicUrl(filePath);

    return publicUrl;
  }

  async promoteToTemplate(project: {
    name: string;
    description: string;
    category: string;
    nodes: Node[];
    edges: Edge[];
    previewFile?: File;
  }): Promise<Template> {

    const templateData = {
      name: project.name,
      description: project.description,
      category: project.category,
      preview: project.previewFile
        ? (await this.uploadTemplatePreview(project.previewFile)).split('/').pop()
        : "empty.png",
      nodes: project.nodes,
      edges: project.edges,
    };

    try {
      const template = await apiService.createTemplate(templateData);
      return template;
    } catch (error) {
      console.error('Template service - Create error:', error);
      throw new Error(`Failed to create template: ${error}`);
    }
  }

  async deleteTemplate(templateId: string): Promise<void> {
    await apiService.deleteTemplate(templateId);
  }

  async updateTemplate(templateId: string, data: UpdateTemplateData): Promise<Template> {
    const updatePayload: Partial<Template> & { preview?: string } = {};

    if (data.name) updatePayload.name = data.name;
    if (data.description) updatePayload.description = data.description;
    if (data.category) updatePayload.category = data.category;
    if (data.nodes) updatePayload.nodes = data.nodes;
    if (data.edges) updatePayload.edges = data.edges;

    // Handle preview image update
    if (data.previewFile) {
      // Upload new file and get the filename (not the full URL)
      const publicUrl = await this.uploadTemplatePreview(data.previewFile);
      updatePayload.preview = publicUrl.split('/').pop(); // Store only the filename
    } else if (data.preview) {
       // If preview filename is provided directly (e.g., keeping existing or setting default)
       updatePayload.preview = data.preview;
    }
    // If neither previewFile nor preview is provided, the preview remains unchanged in the backend

    try {
      const updatedTemplate = await apiService.updateTemplate(templateId, updatePayload);
      // Ensure the returned template has the full preview URL
      return {
        ...updatedTemplate,
        preview: getTemplatePreviewUrl(updatedTemplate.preview),
      };
    } catch (error) {
      console.error('Template service - Update error:', error);
      throw new Error(`Failed to update template: ${error}`);
    }
  }
}

export const templateService = TemplateService.getInstance();
